import { Sheet, VisuallyHidden } from '@hover/blueprint';
import { iconInvite, iconUploadBlueprint, iconUploadPhoto } from '@hover/icons';
import React from 'react';

import { useFeatureAccesses } from 'src/modules/propertyDetails/hooks/useFeatureAccesses';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { emitEvent } from 'src/shared/utils/eventBus';

import { SheetNewMenuItem } from './SheetNewMenuItem';

export enum SheetNewMenuItemAction {
  UPLOAD_BLUEPRINT = 'menu-item-upload-blueprint',
  UPLOAD_EXTERIOR_PHOTOS = 'menu-item-upload-exterior-photos',
  INVITE_TO_CAPTURE = 'menu-item-invite-to-capture',
  CAPTURE_ON_SITE = 'menu-item-capture-on-site',
}

export type SheetNewMenuItemClickEvent = { action: SheetNewMenuItemAction };

export const SHEET_NEW_MENU_ITEM_CLICKED = 'sheet-new-menu-item-clicked';

const SHEET_NEW_HEADER_TEXT = 'Sheet new';

type Props = Pick<React.ComponentProps<typeof Sheet>, 'isOpen' | 'onClose'>;

/**
 * This is the sheet/drawer/side menu that appears when the user clicks on the
 * "+ New" button we are starting to implement.
 * My understanding is that this button will eventually replace smaller buttons we
 * have scattered around the app.
 *
 * Naming is hard so "sheet new" not only is _sounding_ but also what this thing is.
 */
const SheetNew: React.FC<Props> = ({ isOpen, onClose }) => {
  const t = useTranslate(TranslationNamespace.SHARED);

  const menuItemClickHandler = (action: SheetNewMenuItemAction) => {
    emitEvent(SHEET_NEW_MENU_ITEM_CLICKED, { action });
    onClose();
  };

  const { isBlueprintsEnabled } = useFeatureAccesses();

  const shouldRenderBlueprintMenuItem = isBlueprintsEnabled;

  return (
    <Sheet
      header={<VisuallyHidden>{SHEET_NEW_HEADER_TEXT}</VisuallyHidden>}
      isClosable
      isOpen={isOpen}
      onClose={onClose}
    >
      {shouldRenderBlueprintMenuItem && (
        <SheetNewMenuItem
          description={t('navigation.sheetNewUploadBlueprintDescription')}
          icon={iconUploadBlueprint}
          onClick={() =>
            menuItemClickHandler(SheetNewMenuItemAction.UPLOAD_BLUEPRINT)
          }
          title={t('navigation.sheetNewUploadBlueprintTitle')}
        />
      )}

      <SheetNewMenuItem
        description={t('navigation.sheetNewUploadExteriorPhotosDescription')}
        icon={iconUploadPhoto}
        onClick={() =>
          menuItemClickHandler(SheetNewMenuItemAction.UPLOAD_EXTERIOR_PHOTOS)
        }
        title={t('navigation.sheetNewUploadExteriorPhotosTitle')}
      />

      <SheetNewMenuItem
        description={t('navigation.sheetNewInviteToCaptureDescription')}
        icon={iconInvite}
        onClick={() =>
          menuItemClickHandler(SheetNewMenuItemAction.INVITE_TO_CAPTURE)
        }
        title={t('navigation.sheetNewInviteToCaptureTitle')}
      />

      {/* This below will likely soon be added so we're leaving it commented out so it's easier to do so. */}
      {/* It introduces a button to the "sheet new" drawer that takes the user to the app store to download the app. */}

      {/* NOTE: Button that takes you to the app store to download our app */}
      {/* <SheetNewMenuItem */}
      {/*   description="Take photos with the app" */}
      {/*   icon={iconPhone} */}
      {/*   onClick={() => menuItemClickHandler(SheetNewMenuItemAction.CAPTURE_ON_SITE)} */}
      {/*   title="Capture on site" */}
      {/* /> */}
    </Sheet>
  );
};

export { SheetNew };
